/**
 * 🔴 write-intent-registry (US-AI-0311-S03 · @UC9450) — TẬP ĐÓNG các usecase GHI mà LLM được CHỌN khi
 * regex trượt. Đối xứng `read-tool-registry.ts` cho nhánh GHI.
 *
 * 🔴 RANH GIỚI AN TOÀN (giống read-tool-registry — đọc kỹ trước khi sửa):
 *  · LLM CHỌN TÊN usecase trong danh sách hữu hạn (= `AI_CAPABILITY_CATALOG`). **KHÔNG tạo usecase mới,
 *    KHÔNG dựng endpoint.** Validate fail-closed: tên ngoài catalog ⇒ BỎ (coi như chưa chọn).
 *  · LLM chỉ CHỌN BIỂU MẪU để mở — **KHÔNG điền số, KHÔNG viết số tiền**. Slot điền bằng slot-filling
 *    tất định + prefill; con người CONFIRM. Đây là cùng đường `startForm` mà regex mở (AI-INV-1..7).
 *  · Usecase bị admin TẮT (`disabledUsecases`) ⇒ dù LLM chọn vẫn KHÔNG mở (gate ở caller).
 *
 * 🔴 NĂNG LỰC = CODE: registry SINH TỪ `AI_CAPABILITY_CATALOG` ⇒ thêm usecase form ở catalog thì nó tự có
 * mặt ở đây; test `write-intent-registry` ép MỌI usecase catalog phải có mô tả máy-đọc (chống drift — đúng
 * bài học `fund` read-param chết vì đăng ký một nơi mà quên nơi khác).
 *
 * HÀM/HẰNG THUẦN (DP-3): KHÔNG DB, KHÔNG I/O, KHÔNG LLM.
 */
import { AI_CAPABILITY_CATALOG } from "./capability-catalog.js";
import { buildReadToolSystemPrompt, readToolSchemas, type AllowedFund } from "./read-tool-registry.js";

/** Mô tả MÁY-ĐỌC cho LLM chọn đúng usecase (khác `labelVi` marketing-admin). Khoá = intentCode. */
const WRITE_INTENT_DESCRIPTIONS: Readonly<Record<string, string>> = Object.freeze({
  "COL-EQUAL": "Mở đợt thu, mỗi người đóng CÙNG một số tiền (chia đều).",
  "COL-OPEN-AMOUNT": "Mở đợt thu TỰ NGUYỆN, mỗi người đóng tuỳ tâm (không cố định số).",
  "COL-TARGET-SPLIT": "Mở đợt thu theo TỔNG mục tiêu, hệ thống chia đều đầu người.",
  "COL-PER-MEMBER": "Mở đợt thu mỗi người MỘT SỐ RIÊNG (theo danh sách từng người).",
  "COL-TIERED": "Mở đợt thu theo BẬC/HẠNG thành viên (mỗi bậc một mức).",
  "COL-RECURRING": "Mở đợt thu ĐỊNH KỲ tự lặp (hàng tháng/quý/năm).",
  "COL-DEADLINE": "Mở đợt thu có HẠN CHÓT; quá hạn thì thành nợ.",
  "EXP-F-NOALLOC": "Ghi khoản CHI chung: quỹ trả 100%, KHÔNG phân bổ cho ai.",
  "EXP-F-EQUAL": "Ghi khoản CHI từ quỹ, chia đều GÁNH NẶNG cho tất cả.",
  "EXP-F-RATIO": "Ghi khoản CHI từ quỹ, chia gánh theo TỶ LỆ % từng người.",
  "EXP-F-EXCLUDE": "Ghi khoản CHI từ quỹ, chia đều nhưng LOẠI TRỪ một số người.",
  "EXP-P-ADVANCE-EQUAL": "Một người ỨNG tiền túi, cả nhóm chia đều rồi HOÀN lại người ứng.",
  "EXP-P-PAY-FOR-ONE": "Một người TRẢ HỘ đúng MỘT người khác (người đó nợ lại).",
  "VOTE-EXPENSE": "Mở phiên BIỂU QUYẾT cho một khoản CHI đang chờ quyết định.",
  "VOTE-GOVERNANCE":
    "Mở phiên biểu quyết QUẢN TRỊ (đổi quy chế/cấu hình/chuyển chủ nhóm) — KHÔNG đụng tiền.",
  "DEBT-PAY-BEHALF": "Đề xuất TRẢ HỘ khoản thu (đóng tiền quỹ) của người khác.",
  "DEBT-REMIND": "NHẮC những người chưa đóng của một đợt thu.",
  "DEBT-CONFIRM": "Người cho mượn xác nhận đã nhận đủ tiền trả cho một khoản nợ.",
  // 🔴 US-E23.F7-0337 — KHỞI TẠO. Mô tả viết để LLM PHÂN BIỆT ĐƯỢC với họ COL-*/EXP-*: "tạo quỹ" (dựng
  // chỗ chứa tiền, một lần) KHÁC "mở đợt thu" (đi thu tiền, lặp lại). Nhầm hai thứ này là nhầm bản chất.
  "ONB-CREATE-GROUP":
    "Tạo NHÓM MỚI (lớp/CLB/đội) — dựng chỗ để quản lý về sau. KHÔNG phải thu hay chi tiền.",
  "ONB-ADD-MEMBERS":
    "THÊM THÀNH VIÊN vào một nhóm đã có (nhập danh sách tên/email/điện thoại). KHÔNG phải nhắc đóng tiền.",
  "ONB-CREATE-FUND":
    "Tạo QUỸ cho một nhóm (đặt tên + loại quỹ + mức phí/mục tiêu). KHÁC 'mở đợt thu' — đợt thu nằm TRONG quỹ đã có.",
});

export interface WriteIntentSpec {
  /** intentCode = mã usecase catalog (khớp `formIntentCode` router/form dùng). */
  readonly intentCode: string;
  /** Mô tả máy-đọc cho LLM. */
  readonly description: string;
}

/** Registry SINH từ catalog (một nguồn sự thật). Mô tả thiếu ⇒ rơi `labelVi` (test ép phải có mô tả riêng). */
export const WRITE_INTENT_REGISTRY: readonly WriteIntentSpec[] = Object.freeze(
  AI_CAPABILITY_CATALOG.map((c) =>
    Object.freeze({
      intentCode: c.usecaseCode,
      description: WRITE_INTENT_DESCRIPTIONS[c.usecaseCode] ?? c.labelVi,
    }),
  ),
);

const VALID_WRITE_INTENTS: ReadonlySet<string> = new Set(
  WRITE_INTENT_REGISTRY.map((w) => w.intentCode),
);

export function isWriteIntentCode(name: string): boolean {
  return VALID_WRITE_INTENTS.has(name);
}

export function findWriteIntent(name: string): WriteIntentSpec | null {
  return WRITE_INTENT_REGISTRY.find((w) => w.intentCode === name) ?? null;
}

/** Tool-schema cho LLM chọn usecase GHI (mỗi usecase = 1 tool tên = intentCode, giống read-tool). */
export function writeIntentToolSchemas(): readonly {
  readonly name: string;
  readonly description: string;
  readonly inputSchema: Readonly<Record<string, unknown>>;
}[] {
  return WRITE_INTENT_REGISTRY.map((w) => ({
    name: w.intentCode,
    description: w.description,
    inputSchema: Object.freeze({
      type: "object",
      // 🔴 LLM KHÔNG điền số/slot — chỉ CHỌN biểu mẫu. Slot điền bằng form (slot-filling + prefill),
      // người CONFIRM. Vì vậy inputSchema RỖNG (không mời LLM bịa số vào form GHI tiền).
      properties: {},
      required: [],
      additionalProperties: false,
    }),
  }));
}

/**
 * 🔴 System prompt HỢP NHẤT cho fallback: LLM chọn MỘT công cụ — ĐỌC (read-tool) HOẶC MỞ BIỂU MẪU GHI
 * (write-intent) — hoặc KHÔNG chọn gì. Ghép khối read (đã có, gồm khối quỹ) + khối write.
 *
 * Bơm `funds` (US-AI-0311 QĐ PO #2 — vá B1): khối quỹ của read prompt được emit ⇒ LLM chọn được quỹ.
 */
export function buildFallbackSystemPrompt(funds: readonly AllowedFund[] = []): string {
  const writeLines = WRITE_INTENT_REGISTRY.map((w) => `- ${w.intentCode}: ${w.description}`);
  return [
    buildReadToolSystemPrompt(funds),
    "",
    "Ngoài các công cụ ĐỌC trên, bạn còn có thể MỞ BIỂU MẪU GHI (người dùng sẽ tự điền và bấm xác nhận —",
    "bạn CHỈ chọn đúng loại biểu mẫu, TUYỆT ĐỐI không điền số tiền):",
    ...writeLines,
    "",
    "QUY TẮC GHI:",
    "- Chỉ mở biểu mẫu GHI khi câu RÕ là muốn tạo/ghi/mở (thu tiền, ghi chi, mở biểu quyết, nhắc nợ...).",
    "- Câu HỎI hoặc mơ hồ ⇒ KHÔNG mở biểu mẫu GHI (không biến câu hỏi thành hành động tiền).",
    "- Bạn KHÔNG điền số tiền/tên vào biểu mẫu — chỉ chọn đúng loại. Hệ thống sẽ hỏi từng ô.",
  ].join("\n");
}

/** Toàn bộ tool (đọc + ghi) đưa cho LLM ở fallback. */
export function fallbackToolSchemas(): readonly {
  readonly name: string;
  readonly description: string;
  readonly inputSchema: Readonly<Record<string, unknown>>;
}[] {
  return [...readToolSchemas(), ...writeIntentToolSchemas()];
}
